import { EventEmitter } from "./events.js";
import { decodeJwt } from "./jwt.js";
import { defaultFetchHandler, isErrorResponse, XRPC } from "./xrpc.js";
import { ResponseType, httpResponseCodeToEnum } from "./xrpc-utils.js";
export class Agent extends EventEmitter {
  rpc;
  fetch;
  serviceUri;
  session;
  #refreshSessionPromise;
  constructor(options) {
    super();
    this.fetch = options.fetch ?? defaultFetchHandler;
    this.serviceUri = options.serviceUri;
    this.rpc = new XRPC(this.serviceUri);
    this.rpc.fetch = this.#fetch.bind(this);
  }
  async login(options) {
    this.#resetSession();
    const res = await this.rpc.call("com.atproto.server.createSession", {
      data: {
        identifier: options.identifier,
        password: options.password
      }
    });
    return this.#updateSession(res.data);
  }
  async resumeSession(session) {
    const now = Date.now() / 1e3 + 60 * 5;
    const refreshToken = decodeJwt(session.refreshJwt);
    if (now >= refreshToken.exp) {
      throw new Error("INVALID_TOKEN");
    }
    const accessToken = decodeJwt(session.accessJwt);
    this.#resetSession(session);
    if (now >= accessToken.exp) {
      await this.#refreshSession();
    }
    if (!this.session) {
      throw new Error(`INVALID_TOKEN`);
    }
    return this.session;
  }
  async #fetch(httpUri, httpMethod, httpHeaders, httpReqBody, signal) {
    await this.#refreshSessionPromise;
    let res = await this.fetch(httpUri, httpMethod, this.#addAuthHeader(httpHeaders), httpReqBody, signal);
    if (isErrorResponse(res.body, ["ExpiredToken"]) && this.session?.refreshJwt) {
      await this.#refreshSession();
      if (this.session) {
        res = await this.fetch(httpUri, httpMethod, this.#addAuthHeader(httpHeaders), httpReqBody, signal);
      }
    }
    return res;
  }
  #addAuthHeader(httpHeaders) {
    const session = this.session;
    if (!httpHeaders["authorization"] && session) {
      return {
        ...httpHeaders,
        authorization: `Bearer ${session.accessJwt}`
      };
    }
    return httpHeaders;
  }
  async #refreshSession() {
    if (this.#refreshSessionPromise) {
      return this.#refreshSessionPromise;
    }
    this.#refreshSessionPromise = this.#refreshSessionInner();
    try {
      await this.#refreshSessionPromise;
    } finally {
      this.#refreshSessionPromise = void 0;
    }
  }
  async #refreshSessionInner() {
    const session = this.session;
    if (!session || !session.refreshJwt) {
      return;
    }
    const url = new URL(`/xrpc/com.atproto.server.refreshSession`, session.pdsUri || this.serviceUri);
    const res = await this.fetch(
      url.toString(),
      "POST",
      {
        authorization: `Bearer ${session.refreshJwt}`
      },
      void 0,
      void 0
    );
    if (isErrorResponse(res.body, ["ExpiredToken", "InvalidToken"])) {
      this.#resetSession();
      this.emit("sessionExpired");
    } else if (httpResponseCodeToEnum(res.status) === ResponseType.Success) {
      this.#updateSession(res.body);
      this.emit("sessionUpdate", this.session);
    }
  }
  #resetSession(existing) {
    this.session = existing;
    this.rpc.serviceUri = existing && existing.pdsUri || this.serviceUri;
  }
  #updateSession(raw) {
    const didDoc = raw.didDoc;
    let pdsUri;
    if (didDoc) {
      pdsUri = getPdsEndpoint(didDoc);
    }
    this.rpc.serviceUri = pdsUri || this.serviceUri;
    return this.session = {
      accessJwt: raw.accessJwt,
      refreshJwt: raw.refreshJwt,
      handle: raw.handle,
      did: raw.did,
      pdsUri,
      email: raw.email,
      emailConfirmed: raw.emailConfirmed
    };
  }
}
export const getPdsEndpoint = (doc) => {
  return getServiceEndpoint(doc, "#atproto_pds", "AtprotoPersonalDataServer");
};
export const getServiceEndpoint = (doc, serviceId, serviceType) => {
  const did = doc.id;
  const didServiceId = did + serviceId;
  const found = doc.service?.find((service) => service.id === serviceId || service.id === didServiceId);
  if (!found || found.type !== serviceType || typeof found.serviceEndpoint !== "string") {
    return void 0;
  }
  return validateUrl(found.serviceEndpoint);
};
const validateUrl = (urlStr) => {
  let url;
  try {
    url = new URL(urlStr);
  } catch {
    return void 0;
  }
  const proto = url.protocol;
  if (url.hostname && (proto === "http:" || proto === "https:")) {
    return urlStr;
  }
};
